import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, BadgeCheck, Mail, Tags, ScanBarcode, Boxes, Truck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TOOLS = [
  { icon: Tags, name: "Label Manager" },
  { icon: ScanBarcode, name: "Parcel Scan" },
  { icon: Boxes, name: "Inventory Management" },
  { icon: Truck, name: "VMS" },
];

export default function LoginPage() {
  const { login, employeeLogin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("employee");
  const [empId, setEmpId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "employee") await employeeLogin(empId, password);
      else await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => { setMode(m); setError(""); setPassword(""); };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]" data-testid="login-page">
      <div className="relative hidden overflow-hidden lg:block">
        <img src="https://images.unsplash.com/photo-1635468609223-4e59675ac96d?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Warehouse" className="h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/70 to-[#0B0F17]/20" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="font-mono text-xs uppercase tracking-widest text-emerald-400">One sign-in · four tools</div>
          <h1 className="font-heading mt-3 text-4xl font-extrabold uppercase tracking-tight sm:text-5xl lg:text-6xl">StarAndDaisy<br />Operations</h1>
          <div className="mt-6 grid max-w-md grid-cols-2 gap-2">
            {TOOLS.map((t) => (
              <div key={t.name} className="flex items-center gap-2 rounded-sm border border-slate-800 bg-slate-900/60 px-3 py-2 text-sm text-slate-300"><t.icon className="h-4 w-4 text-emerald-400" />{t.name}</div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="rise w-full max-w-md space-y-6" data-testid="login-form">
          <div className="flex items-center gap-4">
            <img src="/starandaisy-logo-white.png" alt="StarAndDaisy" className="h-14 w-auto" data-testid="login-logo" />
            <div>
              <div className="font-heading text-2xl font-extrabold uppercase tracking-tight">Operations</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{mode === "employee" ? "Sign in with your Employee ID" : "Sign in with email (fallback)"}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-1 rounded-sm border border-slate-800 bg-slate-950 p-1" data-testid="login-mode-switch">
            <button type="button" data-testid="login-mode-employee" onClick={() => switchMode("employee")}
              className={`flex items-center justify-center gap-2 rounded-sm px-3 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-200 ${mode === "employee" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>
              <BadgeCheck className="h-4 w-4" /> Employee ID
            </button>
            <button type="button" data-testid="login-mode-email" onClick={() => switchMode("email")}
              className={`flex items-center justify-center gap-2 rounded-sm px-3 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-200 ${mode === "email" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>
              <Mail className="h-4 w-4" /> Email
            </button>
          </div>

          {mode === "employee" ? (
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Employee ID</Label>
              <Input data-testid="login-empid-input" value={empId} onChange={(e) => setEmpId(e.target.value)} required autoFocus placeholder="e.g. EMP001" className="border-slate-700 bg-slate-950 font-mono" />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Email</Label>
              <Input data-testid="login-username-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus className="border-slate-700 bg-slate-950" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Password</Label>
            <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="border-slate-700 bg-slate-950" />
          </div>
          {error && <div data-testid="login-error" className="rounded-sm border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">{error}</div>}
          <Button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
          </Button>
          <p className="text-center text-xs text-slate-500">
            {mode === "employee"
              ? <>Uses your emp.gosnd.com account. Trouble signing in? <button type="button" data-testid="login-fallback-link" className="text-blue-300 hover:underline" onClick={() => switchMode("email")}>Use email sign-in instead</button>.</>
              : "Email accounts are created by an admin under Label Manager → Settings → Accounts."}
          </p>
        </form>
      </div>
    </div>
  );
}
